import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import { authService } from '../../services/authService';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import Icon from '../../components/AppIcon';


import PersonalInfoSection from './components/PersonalInfoSection';
import PreferencesSection from './components/PreferencesSection';
import PasswordChangeSection from './components/PasswordChangeSection';
import AddressManagementSection from './components/AddressManagementSection';

const CustomerProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerProfile, setCustomerProfile] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadCustomerProfile();
  }, [user]);

  const loadCustomerProfile = async () => {
    setLoading(true);
    try {
      const { data, error: profileError } = await customerService?.getCustomerByUserId(user?.id);
      if (profileError) throw profileError;
      setCustomerProfile(data);
    } catch (err) {
      setError(err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (updates) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Address updates should be persisted on the `customers` table
      if (updates?.addresses !== undefined) {
        const { error: updateError } = await customerService?.updateCustomerAddresses(user?.id, updates?.addresses);
        if (updateError) throw updateError;
      } else if (updates?.dietaryRestrictions !== undefined || updates?.cuisinePreferences !== undefined) {
        // Handle preferences update via customerService
        const { error: updateError } = await customerService?.updateCustomerPreferences(user?.id, updates);
        if (updateError) throw updateError;
      } else {
        // Handle profile update via authService (user_profiles table)
        const { error: updateError } = await authService?.updateProfile(updates);
        if (updateError) throw updateError;
      }

      setSuccess('Profile updated successfully!');
      await loadCustomerProfile();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Surface DB error message if present
      console.error('Failed to save profile updates:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' },
    { id: 'password', label: 'Password', icon: 'Lock' },
    { id: 'addresses', label: 'Addresses', icon: 'MapPin' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 mt-20">
        <div className="mb-6">
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
            Edit Profile
          </h1>
          <p className="text-muted-foreground">
            Manage your personal information and preferences
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500 rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-green-500" />
            <p className="text-green-500">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-card rounded-xl shadow-warm p-4 sticky top-24">
              <nav className="space-y-2">
                {sections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth ${
                      activeSection === section?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={section?.icon} size={20} />
                    <span className="font-medium">{section?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-card rounded-xl shadow-warm p-6">
              {activeSection === 'personal' && (
                <PersonalInfoSection
                  profile={customerProfile}
                  onSave={handleSaveProfile}
                  saving={saving}
                />
              )}
              {activeSection === 'preferences' && (
                <PreferencesSection
                  profile={customerProfile}
                  onSave={handleSaveProfile}
                  saving={saving}
                />
              )}
              {activeSection === 'password' && (
                <PasswordChangeSection saving={saving} />
              )}
              {activeSection === 'addresses' && (
                <AddressManagementSection
                  profile={customerProfile}
                  onSave={handleSaveProfile}
                  saving={saving}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileEdit;